import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FirebaseMessaging, Importance, Notification as PushMessage } from '@capacitor-firebase/messaging';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Platform, AlertController, NavController } from '@ionic/angular';
import { PushRegistrationService } from './push-registration.service';
import { pushRoute } from './push-route';

/** Android channel for pushes; the API sends it as `channel_id`. */
const ANDROID_CHANNEL_ID = 'default';

/** White status bar icon for local notifications (`res/drawable`). */
const ANDROID_SMALL_ICON = 'ic_stat_notification';

type PushData = Record<string, string> | undefined;

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private listening = false;

  /** Screen a tap asked for while the app was still starting (welcome routes there). */
  private pendingRoute: string | null = null;

  constructor(
    private platform: Platform,
    private alertCtrl: AlertController,
    private router: Router,
    private nav: NavController,
    private registration: PushRegistrationService,
  ) {}

  /**
   * Push listeners (FCM) as early as possible (AppComponent): the plugin retains the
   * tap that opened the app until there is a listener, so a cold start doesn't lose it.
   */
  async initListeners(): Promise<void> {
    if (this.listening) return;

    const platformReady = await this.platform.ready();
    if (!platformReady || !this.platform.is('capacitor')) return;

    this.listening = true;

    await FirebaseMessaging.addListener('notificationReceived', ({ notification }) =>
      this.onForegroundPush(notification),
    );
    await FirebaseMessaging.addListener('notificationActionPerformed', ({ notification }) =>
      this.openFromPush(pushData(notification)),
    );
    await FirebaseMessaging.addListener('tokenReceived', ({ token }) => this.registration.tokenChanged(token));

    if (this.platform.is('android')) {
      await FirebaseMessaging.createChannel({
        id: ANDROID_CHANNEL_ID,
        name: 'Notificaciones',
        importance: Importance.High,
      });
      await LocalNotifications.addListener('localNotificationActionPerformed', ({ notification }) =>
        this.openFromPush(notification.extra as PushData),
      );
    }
  }

  /**
   * Asks for permission (the OS only shows the prompt once) and, with a session,
   * registers this install's FCM token in the API. Same places that used to
   * initialize OneSignal: after login and when the app starts.
   */
  async handlerNotifications(): Promise<void> {
    const platformReady = await this.platform.ready();
    if (!platformReady || !this.platform.is('capacitor')) return;

    // Idempotent: guarantees the listeners before registering.
    await this.initListeners();

    try {
      const { receive } = await FirebaseMessaging.requestPermissions();
      if (receive === 'granted' && localStorage.getItem('token')) {
        await this.registration.enable();
      }
    } catch (error) {
      console.error('Error al registrar notificaciones push:', error);
    }
  }

  /** Route a tap asked for during startup, once. */
  takePendingRoute(): string | null {
    const route = this.pendingRoute;
    this.pendingRoute = null;
    return route;
  }

  /**
   * Push while the app is open. iOS already shows it (presentationOptions); Android
   * doesn't, so it is shown as a local notification with the same data so the tap
   * opens the same screen.
   */
  private async onForegroundPush(notification: PushMessage): Promise<void> {
    if (!this.platform.is('android')) return;

    try {
      await LocalNotifications.schedule({
        notifications: [
          {
            id: Math.floor(Date.now() % 2_147_483_647),
            title: notification.title ?? '',
            body: notification.body ?? '',
            extra: pushData(notification),
            channelId: ANDROID_CHANNEL_ID,
            smallIcon: ANDROID_SMALL_ICON,
          },
        ],
      });
    } catch {
      // Without notification permission there is no banner.
    }
  }

  private async openFromPush(data: PushData): Promise<void> {
    const profile = JSON.parse(localStorage.getItem('profile') || 'null');
    // Without a session the welcome screen sends the user to login.
    if (!profile) return;

    const route = pushRoute(data?.['url'], profile.current_role?.name);

    // Cold start: the welcome screen is about to route by role; it takes this one instead.
    const url = this.router.url;
    if (url === '/' || url.startsWith('/welcome')) {
      this.pendingRoute = route;
      return;
    }

    try {
      await this.nav.navigateRoot(`/${route}`);
    } catch (error) {
      console.error('Error al navegar desde notificación:', error);
    }
  }

  async showAlert(title = 'Notificación', msg = '', task = 'OK') {
    const alert = await this.alertCtrl.create({
      header: title,
      subHeader: msg,
      buttons: [
        {
          text: `${task}`,
          handler: () => {
            // E.g: Navigate to a specific screen
          },
        },
      ],
    });
    alert.present();
  }
}

/** Push `data`: on iOS it also carries APNs/FCM keys nobody reads. */
function pushData(notification: PushMessage): PushData {
  return (notification.data ?? undefined) as PushData;
}
